import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import api from '../../../services/api';

const useUserGroupApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/user-groups?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/user-groups', formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/user-groups/${id}`, formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      if (ids.length === 1) {
        await api.delete(`/v1/user-groups/${ids[0]}`);
      } else {
        await api.post('/v1/user-groups/batch-delete', ids);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('userGroup.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'isAllUsers', label: t('userGroup.isAllUsers'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['true', 'false'],
          renderValue: (v) => v === 'true' ? t('common.yes') : t('common.no'),
        },
        customFilterListOptions: { render: (v) => v === 'true' ? t('common.yes') : t('common.no') },
        customBodyRender: (v) => v ? <Check size={18} color="#02D3BF" /> : null,
      },
    },
    {
      name: 'groupType', label: t('userGroup.type'),
      options: {
        filter: true, sort: false,
        filterType: 'dropdown',
        filterOptions: {
          defaultOp: 'eq',
          names: ['0', '1'],
          renderValue: (v) => v === '1' ? t('userGroup.typeDynamic') : t('userGroup.typeStatic'),
        },
        customFilterListOptions: {
          render: (v) => v === '1' ? t('userGroup.typeDynamic') : t('userGroup.typeStatic'),
        },
        customBodyRender: (v) => (
          <TypeBadge $dynamic={v === 1}>
            {v === 1 ? t('userGroup.typeDynamic') : t('userGroup.typeStatic')}
          </TypeBadge>
        ),
      },
    },
    { name: 'memberCount', label: t('userGroup.memberCount'), options: { filter: false, sort: false } },
  ], [t]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('userGroup.name'), type: 'text', grid: 12, required: true },
    {
      name: 'groupType', label: t('userGroup.type'), type: 'select', grid: 12, required: true,
      // Yeni grup STATIK on secili acilir: aksi halde kullanici ad yazip kaydetmek
      // isterken "zorunlu alan" hatasi alir ve bugunku akis gereksiz yere degisir.
      // (GenericSelect 0 degerini bos SAYMAZ, GenericFormDrawer'in required
      //  dogrulamasi da 0'i gecirir.)
      defaultValue: 0,
      options: [
        { id: 0, name: t('userGroup.typeStatic') },
        { id: 1, name: t('userGroup.typeDynamic') },
      ],
      idKey: 'id', labelKey: 'name',
    },
    {
      name: 'groupTypeNotice', type: 'notice', grid: 12,
      content: t('userGroup.dynamicNotice'),
      // Yalnız dinamik seçiliyken görünür: statik grupta bu açıklama gürültü olur.
      hidden: (formData) => Number(formData?.groupType) !== 1,
    },
    {
      name: 'criteria', type: 'userGroupCriteria', grid: 12,
      label: t('userGroup.criteria'),
      defaultValue: [],
      hidden: (formData) => Number(formData?.groupType) !== 1,
    },
  ], [t]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

const TypeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 10px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  background: ${({ $dynamic }) => ($dynamic ? 'rgba(2, 211, 191, 0.12)' : '#F5F7FA')};
  color: ${({ $dynamic }) => ($dynamic ? '#008B7D' : '#64748b')};
`;

export default useUserGroupApi;
